import { useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { useAdminNewsFeatures } from "@/hooks/useNewsFeatures";
import {
  createNewsFeature,
  deleteNewsFeature,
  updateNewsFeature,
} from "@/lib/api";
import type { NewsFeatureRow } from "@/lib/schema";
import { FileUpload } from "./FileUpload";
import { AdminPanelHeader } from "./AdminPanelHeader";

const emptyForm = {
  title: "",
  description: "",
  thumbnail_url: "",
  link_url: "",
  link_label: "Read more",
  sort_order: "0",
  is_published: true,
};

type FormState = typeof emptyForm;

export function NewsFeaturesManager() {
  const { data: items, loading, refetch } = useAdminNewsFeatures();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setIsFormOpen(true);
  };

  const returnToList = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setIsFormOpen(false);
  };

  const startEdit = (item: NewsFeatureRow) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.description ?? "",
      thumbnail_url: item.thumbnail_url,
      link_url: item.link_url,
      link_label: item.link_label || "Read more",
      sort_order: String(item.sort_order),
      is_published: item.is_published,
    });
    setError(null);
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.thumbnail_url.trim() || !form.link_url.trim()) {
      setError("Title, thumbnail image, and link URL are required.");
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      thumbnail_url: form.thumbnail_url.trim(),
      link_url: form.link_url.trim(),
      link_label: form.link_label.trim() || "Read more",
      sort_order: Number(form.sort_order) || 0,
      is_published: form.is_published,
    };

    const result = editingId
      ? await updateNewsFeature(editingId, payload)
      : await createNewsFeature(payload);

    setSaving(false);

    if (result.error) {
      setError(result.error.message);
      return;
    }

    returnToList();
    refetch();
  };

  const handleDelete = async (item: NewsFeatureRow) => {
    if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    setDeleting(item.id);
    await deleteNewsFeature(item.id);
    setDeleting(null);
    if (editingId === item.id) returnToList();
    refetch();
  };

  return (
    <div>
      <div className="mb-6">
        <AdminPanelHeader
          title="News & Features"
          description="Manage the News & Features cards shown on the Our Story page."
          action={
            !isFormOpen ? (
              <button
                type="button"
                onClick={startNew}
                className="inline-flex items-center gap-2 rounded-xl bg-pink-dark px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95"
              >
                <Plus className="h-4 w-4" />
                New Item
              </button>
            ) : null
          }
        />
      </div>

      {isFormOpen ? (
        <div className="mb-8 rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingId ? "Edit item" : "Add item"}
            </h2>
            <button
              type="button"
              onClick={returnToList}
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-pink-dark"
            >
              <X className="h-4 w-4" />
              Back to all items
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-gray-700">Title/headline *</span>
              <input
                type="text"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </label>

            <label className="sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-gray-700">Short description</span>
              <textarea
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </label>

            <label className="sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-gray-700">Thumbnail image URL *</span>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="url"
                  value={form.thumbnail_url}
                  onChange={(event) => setForm((prev) => ({ ...prev, thumbnail_url: event.target.value }))}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                  placeholder="https://..."
                />
                <FileUpload
                  folder="news-features"
                  accept="image/*"
                  label="Upload image"
                  disabled={saving}
                  onUploaded={(url) => setForm((prev) => ({ ...prev, thumbnail_url: url }))}
                />
              </div>
            </label>

            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Full article / podcast / feature URL *</span>
              <input
                type="url"
                value={form.link_url}
                onChange={(event) => setForm((prev) => ({ ...prev, link_url: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                placeholder="https://... or /internal-page"
              />
            </label>

            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Link label</span>
              <input
                type="text"
                value={form.link_label}
                onChange={(event) => setForm((prev) => ({ ...prev, link_label: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </label>

            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Sort order</span>
              <input
                type="number"
                value={form.sort_order}
                onChange={(event) => setForm((prev) => ({ ...prev, sort_order: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </label>

            <label className="flex items-center gap-2 pt-6 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(event) => setForm((prev) => ({ ...prev, is_published: event.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-pink-dark focus:ring-pink-dark"
              />
              Published on Our Story page
            </label>
          </div>

          {form.thumbnail_url && (
            <div className="mt-4 flex items-center gap-3 rounded-xl bg-white p-3 text-sm text-gray-600">
              <img src={form.thumbnail_url} alt="" className="h-16 w-24 rounded-lg object-cover" />
              <span>Thumbnail preview</span>
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl bg-pink-dark px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-px disabled:opacity-50 active:scale-95"
            >
              {saving ? "Saving…" : editingId ? "Update Item" : "Create Item"}
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-left text-sm">
            <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Sort</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                    Loading news & features…
                  </td>
                </tr>
              )}
              {!loading &&
                (items ?? []).map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={item.thumbnail_url} alt="" className="h-12 w-16 rounded-md object-cover" />
                        <div className="min-w-0">
                          <p className="truncate font-medium text-gray-900">{item.title}</p>
                          <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="truncate text-xs text-gray-500 hover:text-pink-dark">
                            {item.link_url}
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${item.is_published ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {item.is_published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{item.sort_order}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-pink-dark"
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={deleting === item.id}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {!loading && (!items || items.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                    No news or feature items yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
